import styled from "styled-components";
import { IoMdClose } from "react-icons/io";
import { BsFillExclamationCircleFill, BsFillQuestionCircleFill } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { getGeocode, getLatLng } from "use-places-autocomplete";
import Btn from "../shared/button/Btn";
import AbsoluteLocationMap from "../map/AbsoluteLocationMap";
import {
  getAdresAutoComplete,
  setAdresAutoComplete,
} from "../../features/address/addressSlice";
import { handleActiveSelect } from "../../features/layout/layoutSlice";
import locationData from "../../utils/locationData/locationOptions";
import { showNestedModal } from "../../features/modal/modalSlice";
import InputSelectAdres from "../shared/input/special/InputSelectAdres";
import smoothPanAndZoom from "../../helper/maps/smoothPanAndZoom";
import { updateAddressData } from "../../features/registeredAddress/registeredAddressSlice";

const Container = styled.div`
  position: fixed;
  inset: 0;
  z-index: 27;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);

  .modal {
    width: auto;
    height: auto;
    padding: 20px;
    border-radius: 10px;
    color: ${({ theme }) => theme.mainTextColor};
    background: ${({ theme }) => theme.mainBgColor};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .content {
    width: 750px;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .top-container,
  .input-container,
  .bottom-container,
  .btn-container,
  .question-container {
    display: flex;
    align-items: center;
  }

  .top-container,
  .input-container,
  .bottom-container {
    justify-content: space-between;
  }

  .top-container {
    margin-bottom: 10px;
  }

  .h2 {
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-xl);
  }

  .close-icon {
    color: ${({ theme }) => theme.mainTextColor};
    cursor: pointer;
    font-size: 1.4rem;
  }

  .desc {
    margin-bottom: 30px;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-md);
  }

  .input-container {
    margin-bottom: 20px;
  }

  .exc-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px 10px;
    background: ${({ theme }) => theme.secondaryBgColor};
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-lg);
  }

  .exc-icon {
    margin-right: 5px;
    fill: #636161;
  }

  .map-container {
    width: 100%;
    min-width: 400px;
    height: 350px;
  }

  .bottom-container {
    margin-top: 20px;
  }

  .adres-text {
    max-width: 520px;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-lg);
  }

  .btn-container {
    gap: 5px;
  }

  .question-container {
    position: relative;
  }

  .question-icon {
    color: ${({ theme, $hovered }) =>
      $hovered ? theme.mainTextColor : theme.secondaryTextColor};
    cursor: pointer;
    font-size: 1.4rem;
  }

  .desc-container {
    position: absolute;
    top: 0;
    right: -170px;
    width: 400px;
    padding: 10px;
    border-radius: 10px;
    transform: translate(-50%, -50%);
    background: ${({ theme }) => theme.secondaryBgColor};
    z-index: 4;
  }

  .desc-container p {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-md);
    line-height: 1.4;
  }

  @media (max-width: 800px) {
    .modal {
      width: 100vw;
      min-height: 100dvh;
      border-radius: 0;
    }

    .content {
      width: 100%;
    }

    .map-container {
      min-width: 0;
    }

    .bottom-container {
      align-items: stretch;
      flex-direction: column;
      gap: 15px;
    }

    .adres-text {
      max-width: none;
    }
  }
`;

const findLocationFromAddress = (googleAddress) => {
  if (!googleAddress) return null;
  const normalizedAddress = googleAddress.toLocaleLowerCase("tr-TR");
  return locationData.find((item) => {
    const city = item.city?.split(" ")[0]?.toLocaleLowerCase("tr-TR");
    const town = item.town?.toLocaleLowerCase("tr-TR");
    return city && town && normalizedAddress.includes(city) && normalizedAddress.includes(town);
  });
};

const KayitliAdresMapModal = () => {
  const { addressData } = useSelector((state) => state.registeredAddress);
  const { adresAutoCompleteOptions } = useSelector((state) => state.address);
  const mapRef = useRef(null);
  const dispatch = useDispatch();
  const [updatedLocationItem, setUpdatedLocationItem] = useState(addressData);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [hoveredQuestionText, setHoveredQuestionText] = useState(false);
  const [searchText, setSearchText] = useState(
    addressData?.locationData?.googleAddress ?? ""
  );

  useEffect(() => {
    const query = searchText.trim();
    if (!query) {
      dispatch(setAdresAutoComplete([]));
      return;
    }
    if (query.length < 2 || selectedAddress?.description === query) return;

    const timeoutId = setTimeout(() => {
      dispatch(getAdresAutoComplete({ searchText: query }));
    }, 600);
    return () => clearTimeout(timeoutId);
  }, [dispatch, searchText, selectedAddress?.description]);

  useEffect(() => {
    if (!selectedAddress?.description) return;

    const getCoordinates = async () => {
      try {
        const results = await getGeocode({ address: selectedAddress.description });
        const { lat, lng } = await getLatLng(results[0]);
        setSearchText(selectedAddress.description);
        setUpdatedLocationItem((current) => ({
          ...current,
          locationData: {
            ...current.locationData,
            lat,
            lng,
            googleAddress: selectedAddress.description,
            isFirstChange: true,
          },
        }));
        smoothPanAndZoom({ lat, lng }, 18, 200, mapRef);
        dispatch(handleActiveSelect(null));
      } catch (_) {
        // Arama sonucu koordinata çevrilemezse kullanıcı haritadan devam edebilir.
      }
    };

    getCoordinates();
  }, [dispatch, selectedAddress]);

  const handleClose = () => {
    dispatch(setAdresAutoComplete([]));
    dispatch(showNestedModal(null));
  };

  const handleSubmit = () => {
    const selectedLocation =
      findLocationFromAddress(updatedLocationItem?.locationData?.googleAddress) ??
      addressData?.locationData;

    dispatch(
      updateAddressData({
        ...updatedLocationItem,
        address:
          addressData?.address || updatedLocationItem?.locationData?.googleAddress || "",
        locationData: {
          ...addressData?.locationData,
          ...selectedLocation,
          googleAddress: updatedLocationItem?.locationData?.googleAddress ?? "",
          lat: updatedLocationItem?.locationData?.lat,
          lng: updatedLocationItem?.locationData?.lng,
        },
      })
    );
    dispatch(setAdresAutoComplete([]));
    dispatch(showNestedModal(null));
  };

  const selectedGoogleAddress = updatedLocationItem?.locationData?.googleAddress;

  return (
    <Container $hovered={hoveredQuestionText}>
      <div
        className="modal"
        onClick={(event) => {
          event.stopPropagation();
          dispatch(handleActiveSelect(null));
        }}
      >
        <div className="content">
          <div className="top-container">
            <h2 className="h2">Tam konumunuz nedir?</h2>
            <IoMdClose className="close-icon" onClick={handleClose} />
          </div>
          <p className="desc">
            Tam konum girmeniz sorunsuz sevkiyat işlemi ve sefer konum takibi için önemlidir.
          </p>

          <div className="input-container">
            <InputSelectAdres
              text={searchText}
              setText={(event) => setSearchText(event.target.value)}
              setSelected={setSelectedAddress}
              labelText="Adres Arayın"
              name="registeredAddressSearch"
              options={adresAutoCompleteOptions}
              inputWidth={searchText.length > 0 ? "calc(100% - 50px)" : "100%"}
            />
            {searchText.length > 0 && (
              <button type="button" style={{ textDecoration: "underline", color: "var(--color-main)", fontSize: 12, fontWeight: 500 }} onClick={() => setSearchText("")}>
                Temizle
              </button>
            )}
          </div>

          <div className="exc-container">
            <BsFillExclamationCircleFill className="exc-icon" />
            Haritada imleci seçmek istediğiniz lokasyona getirin
          </div>

          <div className="map-container">
            <AbsoluteLocationMap
              mapRef={mapRef}
              updatedLocationItem={updatedLocationItem}
              setUpdatedLocationItem={setUpdatedLocationItem}
              setSearchText={setSearchText}
            />
          </div>

          <div className="bottom-container">
            <span className="adres-text">
              {selectedGoogleAddress
                ? `Seçilen Adres: ${selectedGoogleAddress}`
                : "Adres Girin"}
            </span>
            <div className="btn-container">
              <div className="question-container">
                <BsFillQuestionCircleFill
                  className="question-icon"
                  onMouseEnter={() => setHoveredQuestionText(true)}
                  onMouseLeave={() => setHoveredQuestionText(false)}
                />
                {hoveredQuestionText && (
                  <div className="desc-container">
                    <p>
                      Haritadan kaydedilen konum sürücünün gideceği tam konum olarak kullanılacaktır.
                    </p>
                  </div>
                )}
              </div>
              <Btn
                width="140px"
                color="#fff"
                bgColor={selectedGoogleAddress ? "#E85446" : "#7c7b7b"}
                isDisabled={!selectedGoogleAddress}
                handleClick={handleSubmit}
                borderColor="none"
              >
                Bu Adresi Kullan
              </Btn>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default KayitliAdresMapModal;
